import { useState, useEffect, useMemo, useCallback } from 'react'
import { pipelineService, activityService } from '../services/AdminServices'
import { adminApi } from '../services/ApiService'
import { useAuth } from '../context/AuthContext'
import {
  TrendingUp, Plus, Trash2, Save, X, Edit3, AlertTriangle,
  User, Users, ChevronRight, Target, Clock, Phone,
  Mail, Calendar, BarChart3, ArrowRight, GripVertical,
  CheckCircle, Search, ArrowUpRight, ArrowDownRight
} from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import './PipelineHub.css'

const STAGES = [
  { value: 'lead', label: 'Lead', color: '#94a3b8', prob: 10 },
  { value: 'contacto', label: 'Contacto', color: '#60a5fa', prob: 25 },
  { value: 'demo', label: 'Demo', color: '#a78bfa', prob: 50 },
  { value: 'propuesta', label: 'Propuesta', color: '#f59e0b', prob: 65 },
  { value: 'negociacion', label: 'Negociación', color: '#fb923c', prob: 80 },
  { value: 'cerrado_ganado', label: 'Ganado', color: '#4ade80', prob: 100 },
  { value: 'cerrado_perdido', label: 'Perdido', color: '#f87171', prob: 0 },
]
const ACTIVE_STAGES = STAGES.filter(s => !['cerrado_ganado', 'cerrado_perdido'].includes(s.value))
const PRODUCTS = ['GP-BOX', 'GP-MINI', 'GP-CAM', 'GP-HIDRO', 'GP-RELAY', 'GP-DIM', 'Kit Completo', 'Suscripción Básico', 'Suscripción Pro', 'Otro']
const PRIORITIES = [
  { value: 'high', label: 'Alta', color: '#f87171', icon: '🔴' },
  { value: 'medium', label: 'Media', color: '#f59e0b', icon: '🟡' },
  { value: 'low', label: 'Baja', color: '#94a3b8', icon: '⚪' },
]
const LOSS_REASONS = ['Precio', 'Competencia', 'Sin presupuesto', 'Sin respuesta', 'No es el momento', 'Funcionalidad', 'Otro']

function formatCurrency(n) {
  if (!n) return '$0'
  return '$' + Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })
}
function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })
}
function formatDateFull(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}
function getStageInfo(stage) {
  return STAGES.find(s => s.value === stage) || STAGES[0]
}
function daysInStage(deal) {
  const d = deal.stageChangedAt || deal.lastContact || deal.$createdAt
  if (!d) return null
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000)
}

export default function PipelineHub() {
  const { user } = useAuth()
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [view, setView] = useState('kanban')
  const [editingId, setEditingId] = useState(null)
  const [adminUsers, setAdminUsers] = useState([])
  const [selectedOwners, setSelectedOwners] = useState([]) // empty = all
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [stageFilter, setStageFilter] = useState('all')
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [dealActivities, setDealActivities] = useState([])
  const [dragDeal, setDragDeal] = useState(null)
  const [dragOverStage, setDragOverStage] = useState(null)
  const [form, setForm] = useState({
    title: '', clientName: '', clientEmail: '', clientPhone: '', stage: 'lead',
    value: '', product: '', notes: '', probability: 10, lastContact: '', owner: '',
    priority: 'medium', nextAction: '', nextActionDate: '', source: '', lossReason: ''
  })

  const loadDeals = useCallback(async () => {
    try {
      setLoading(true)
      const res = await pipelineService.list()
      setDeals(res.deals || res.documents || [])
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadDeals() }, [loadDeals])
  useEffect(() => {
    adminApi.get('/admin/admin-users').then(r => setAdminUsers(r.users || [])).catch(() => { })
  }, [])

  const allOwnersSelected = selectedOwners.length === 0 || (adminUsers.length > 0 && selectedOwners.length === adminUsers.length)
  const isMineOnly = selectedOwners.length === 1 && selectedOwners[0] === user?.email

  function toggleOwner(email) {
    setSelectedOwners(prev => {
      const current = prev.length === 0 ? adminUsers.map(u => u.email) : [...prev]
      return current.includes(email) ? current.filter(e => e !== email) : [...current, email]
    })
  }

  function toggleMine() {
    if (isMineOnly) {
      setSelectedOwners([])
    } else {
      setSelectedOwners([user?.email].filter(Boolean))
    }
  }

  function selectAllOwners() {
    setSelectedOwners([])
  }

  const filteredDeals = useMemo(() => {
    let d = deals
    if (selectedOwners.length > 0 && adminUsers.length > 0 && selectedOwners.length < adminUsers.length) {
      d = d.filter(x => selectedOwners.includes(x.owner))
    }
    if (stageFilter !== 'all') d = d.filter(x => x.stage === stageFilter)
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      d = d.filter(x =>
        (x.title || '').toLowerCase().includes(q) ||
        (x.clientName || '').toLowerCase().includes(q) ||
        (x.clientEmail || '').toLowerCase().includes(q) ||
        (x.product || '').toLowerCase().includes(q)
      )
    }
    return d
  }, [deals, selectedOwners, adminUsers, stageFilter, searchTerm])

  const dealsByStage = useMemo(() => {
    const map = {}
    STAGES.forEach(s => { map[s.value] = [] })
    filteredDeals.forEach(d => {
      const stage = d.stage || 'lead'
      if (map[stage]) map[stage].push(d)
      else map.lead.push(d)
    })
    return map
  }, [filteredDeals])

  const stats = useMemo(() => {
    const fd = filteredDeals
    const active = fd.filter(d => !['cerrado_ganado', 'cerrado_perdido'].includes(d.stage))
    const totalPipeline = active.reduce((s, d) => s + (d.value || 0), 0)
    const weighted = active.reduce((s, d) => s + (d.value || 0) * ((d.probability || 0) / 100), 0)
    const won = fd.filter(d => d.stage === 'cerrado_ganado')
    const lost = fd.filter(d => d.stage === 'cerrado_perdido')
    const totalWon = won.reduce((s, d) => s + (d.value || 0), 0)
    const totalLost = lost.reduce((s, d) => s + (d.value || 0), 0)
    const closed = won.length + lost.length
    const convRate = closed > 0 ? ((won.length / closed) * 100).toFixed(0) : 0
    const avgDealSize = won.length > 0 ? totalWon / won.length : 0
    const staleDeals = active.filter(d => daysInStage(d) > 14).length
    return { total: fd.length, active: active.length, totalPipeline, weighted, totalWon, totalLost, convRate, won: won.length, lost: lost.length, avgDealSize, staleDeals }
  }, [filteredDeals])

  const funnelData = useMemo(() => {
    return ACTIVE_STAGES.map(s => ({
      name: s.label,
      value: (dealsByStage[s.value] || []).reduce((sum, d) => sum + (d.value || 0), 0),
      count: (dealsByStage[s.value] || []).length,
      fill: s.color,
    }))
  }, [dealsByStage])

  const forecastData = useMemo(() => {
    const active = filteredDeals.filter(d => !['cerrado_ganado', 'cerrado_perdido'].includes(d.stage))
    return ACTIVE_STAGES.map(s => {
      const stageDeals = active.filter(d => d.stage === s.value)
      const total = stageDeals.reduce((sum, d) => sum + (d.value || 0), 0)
      const weighted = stageDeals.reduce((sum, d) => sum + (d.value || 0) * ((d.probability || s.prob) / 100), 0)
      return { name: s.label, total, weighted, color: s.color, count: stageDeals.length }
    })
  }, [filteredDeals])

  // Drag & Drop
  function handleDragStart(e, deal) {
    setDragDeal(deal)
    e.dataTransfer.effectAllowed = 'move'
  }
  function handleDragOver(e, stageValue) {
    e.preventDefault()
    setDragOverStage(stageValue)
  }
  function handleDragLeave() { setDragOverStage(null) }
  async function handleDrop(e, stageValue) {
    e.preventDefault()
    setDragOverStage(null)
    if (dragDeal && dragDeal.stage !== stageValue) await moveDeal(dragDeal, stageValue)
    setDragDeal(null)
  }

  function openForm(deal) {
    if (deal) {
      setEditingId(deal.$id)
      setForm({
        title: deal.title || '', clientName: deal.clientName || '', clientEmail: deal.clientEmail || '',
        clientPhone: deal.clientPhone || '', stage: deal.stage || 'lead', value: deal.value || '',
        product: deal.product || '', notes: deal.notes || '', probability: deal.probability ?? 50,
        lastContact: deal.lastContact || '', owner: deal.owner || '', priority: deal.priority || 'medium',
        nextAction: deal.nextAction || '', nextActionDate: deal.nextActionDate || '',
        source: deal.source || '', lossReason: deal.lossReason || ''
      })
    } else {
      setEditingId(null)
      setForm({
        title: '', clientName: '', clientEmail: '', clientPhone: '', stage: 'lead',
        value: '', product: '', notes: '', probability: 10,
        lastContact: new Date().toISOString().substring(0, 10), owner: '',
        priority: 'medium', nextAction: '', nextActionDate: '', source: '', lossReason: ''
      })
    }
    setView('form')
  }

  async function openDetail(deal) {
    setSelectedDeal(deal)
    setView('detail')
    try {
      const res = await activityService.list({ clientName: deal.clientName })
      setDealActivities((res.activities || res.documents || []).slice(0, 10))
    } catch { setDealActivities([]) }
  }

  async function saveDeal() {
    if (!form.title.trim()) { setError('Título requerido'); return }
    try {
      const data = { ...form, value: Number(form.value) || 0, probability: Number(form.probability) || 0 }
      if (editingId) await pipelineService.update(editingId, data)
      else await pipelineService.create(data)
      setView('kanban')
      setEditingId(null)
      loadDeals()
    } catch (e) { setError(e.message) }
  }

  async function deleteDeal(id) {
    if (!confirm('¿Eliminar este deal?')) return
    try {
      await pipelineService.delete(id)
      loadDeals()
      if (selectedDeal?.$id === id) setView('kanban')
    } catch (e) { setError(e.message) }
  }

  async function moveDeal(deal, newStage) {
    try {
      const data = { stage: newStage, stageChangedAt: new Date().toISOString() }
      if (newStage === 'cerrado_ganado' || newStage === 'cerrado_perdido') data.closedAt = new Date().toISOString()
      const stageInfo = STAGES.find(s => s.value === newStage)
      if (stageInfo) data.probability = stageInfo.prob
      await pipelineService.update(deal.$id, data)
      loadDeals()
    } catch (e) { setError(e.message) }
  }

  async function quickSetNextAction(deal, action) {
    try {
      await pipelineService.update(deal.$id, { nextAction: action, nextActionDate: new Date().toISOString().substring(0, 10) })
      loadDeals()
    } catch (e) { setError(e.message) }
  }

  if (loading && deals.length === 0) {
    return <div className="pip-page"><div className="pip-loading"><div className="pip-spinner" /> Cargando pipeline...</div></div>
  }

  return (
    <div className="pip-page">
      {/* HEADER */}
      <div className="pip-header">
        <div className="pip-header-left">
          <TrendingUp size={28} />
          <div>
            <h1>Pipeline de Ventas</h1>
            <p className="pip-subtitle">{stats.active} oportunidades activas · {formatCurrency(stats.totalPipeline)} en pipeline</p>
          </div>
        </div>
        <div className="pip-header-actions">
          <button className={`pip-btn ${isMineOnly ? 'pip-btn-primary' : 'pip-btn-secondary'}`} onClick={toggleMine}>
            <User size={16} /> Mis deals
          </button>
          {adminUsers.length > 0 && (
            <div className="owner-multiselect">
              <button className="owner-multiselect-trigger" onClick={() => setShowOwnerDropdown(v => !v)}>
                <Users size={14} />
                {allOwnersSelected ? 'Todo el equipo' : `${selectedOwners.length} seleccionado${selectedOwners.length !== 1 ? 's' : ''}`}
              </button>
              {showOwnerDropdown && (
                <div className="owner-multiselect-dropdown">
                  <div className="owner-multiselect-item" onClick={selectAllOwners}>
                    <input type="checkbox" checked={allOwnersSelected} readOnly />
                    <span>👥 Todo el equipo</span>
                  </div>
                  {adminUsers.map(u => {
                    const emails = selectedOwners.length === 0 ? adminUsers.map(x => x.email) : selectedOwners
                    const checked = emails.includes(u.email)
                    return (
                      <div key={u.$id} className="owner-multiselect-item" onClick={() => toggleOwner(u.email)}>
                        <input type="checkbox" checked={checked} readOnly />
                        <span>{u.name || u.email}</span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
          <button className="pip-btn pip-btn-primary" onClick={() => openForm()}>
            <Plus size={16} /> Nuevo Deal
          </button>
        </div>
      </div>

      {error && <div className="pip-error"><AlertTriangle size={16} /> {error} <button onClick={() => setError(null)}><X size={14} /></button></div>}

      {/* STATS */}
      <div className="pip-stats">
        <div className="pip-stat"><span className="pip-stat-num">{stats.total}</span><span className="pip-stat-label">Total Deals</span></div>
        <div className="pip-stat"><span className="pip-stat-num" style={{ color: '#60a5fa' }}>{formatCurrency(stats.totalPipeline)}</span><span className="pip-stat-label">Pipeline Activo</span></div>
        <div className="pip-stat"><span className="pip-stat-num" style={{ color: '#a78bfa' }}>{formatCurrency(stats.weighted)}</span><span className="pip-stat-label">Ponderado</span></div>
        <div className="pip-stat"><span className="pip-stat-num" style={{ color: '#4ade80' }}>{formatCurrency(stats.totalWon)}</span><span className="pip-stat-label">Ganados</span></div>
        <div className="pip-stat"><span className="pip-stat-num" style={{ color: '#f59e0b' }}>{stats.convRate}%</span><span className="pip-stat-label">Win Rate</span></div>
        <div className="pip-stat"><span className="pip-stat-num" style={{ color: '#4ade80' }}>{formatCurrency(stats.avgDealSize)}</span><span className="pip-stat-label">Ticket Promedio</span></div>
        {stats.staleDeals > 0 && (
          <div className="pip-stat pip-stat-alert"><span className="pip-stat-num" style={{ color: '#f87171' }}>{stats.staleDeals}</span><span className="pip-stat-label">⚠️ Estancados</span></div>
        )}
      </div>

      {/* TOOLBAR */}
      <div className="pip-toolbar">
        <div className="pip-view-toggle">
          <button className={view === 'kanban' ? 'active' : ''} onClick={() => setView('kanban')}><Target size={16} /> Kanban</button>
          <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}><ChevronRight size={16} /> Lista</button>
          <button className={view === 'forecast' ? 'active' : ''} onClick={() => setView('forecast')}><BarChart3 size={16} /> Forecast</button>
        </div>
        <div className="pip-filters">
          <div className="pip-search"><Search size={14} /><input placeholder="Buscar deal..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} /></div>
          <select className="pip-stage-filter" value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
            <option value="all">Todas las etapas</option>
            {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* ═══ KANBAN ═══ */}
      {view === 'kanban' && (
        <div className="pip-kanban">
          {STAGES.filter(s => s.value !== 'cerrado_perdido').map(stage => (
            <div key={stage.value} className={`pip-column ${dragOverStage === stage.value ? 'pip-column-dragover' : ''}`}
              onDragOver={e => handleDragOver(e, stage.value)} onDragLeave={handleDragLeave} onDrop={e => handleDrop(e, stage.value)}>
              <div className="pip-column-header" style={{ borderTopColor: stage.color }}>
                <span>{stage.label}</span>
                <div className="pip-column-meta">
                  <span className="pip-column-count">{dealsByStage[stage.value]?.length || 0}</span>
                  <span className="pip-column-total">{formatCurrency((dealsByStage[stage.value] || []).reduce((s, d) => s + (d.value || 0), 0))}</span>
                </div>
              </div>
              <div className="pip-column-body">
                {(dealsByStage[stage.value] || []).map(deal => {
                  const days = daysInStage(deal)
                  const isStale = days > 14 && !['cerrado_ganado', 'cerrado_perdido'].includes(deal.stage)
                  const prioInfo = PRIORITIES.find(p => p.value === deal.priority) || PRIORITIES[1]
                  return (
                    <div key={deal.$id} className={`pip-card ${isStale ? 'pip-card-stale' : ''}`}
                      draggable onDragStart={e => handleDragStart(e, deal)} onClick={() => openDetail(deal)}>
                      <div className="pip-card-top">
                        <span className="pip-card-priority" title={prioInfo.label}>{prioInfo.icon}</span>
                        <div className="pip-card-title">{deal.title}</div>
                        <GripVertical size={12} className="pip-card-grip" />
                      </div>
                      {deal.clientName && <div className="pip-card-client"><User size={11} /> {deal.clientName}</div>}
                      <div className="pip-card-row">
                        {deal.value > 0 && <span className="pip-card-value">{formatCurrency(deal.value)}</span>}
                        {deal.product && <span className="pip-card-product">{deal.product}</span>}
                      </div>
                      {deal.nextAction && <div className="pip-card-next"><CheckCircle size={11} /> {deal.nextAction}</div>}
                      <div className="pip-card-footer">
                        {deal.owner && <span className="pip-card-owner">{deal.owner.split('@')[0]}</span>}
                        {days != null && <span className={`pip-card-days ${isStale ? 'stale' : ''}`}><Clock size={10} /> {days}d</span>}
                      </div>
                      <div className="pip-card-prob">
                        <div className="pip-prob-bar"><div style={{ width: `${deal.probability || 0}%`, background: stage.color }} /></div>
                        <span>{deal.probability || 0}%</span>
                      </div>
                      <div className="pip-card-quick-actions" onClick={e => e.stopPropagation()}>
                        {stage.value !== 'cerrado_ganado' && (
                          <select className="pip-move-select" value="" onChange={e => { if (e.target.value) moveDeal(deal, e.target.value) }}>
                            <option value="">Mover →</option>
                            {STAGES.filter(s => s.value !== stage.value).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </select>
                        )}
                        <button onClick={() => openForm(deal)} title="Editar"><Edit3 size={12} /></button>
                      </div>
                    </div>
                  )
                })}
                {(dealsByStage[stage.value] || []).length === 0 && <div className="pip-column-empty">Sin deals</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ LIST ═══ */}
      {view === 'list' && (
        <div className="pip-list-container">
          <table className="pip-table">
            <thead><tr>
              <th>Prio</th><th>Deal</th><th>Cliente</th><th>Owner</th><th>Producto</th><th>Valor</th>
              <th>Prob.</th><th>Etapa</th><th>Días</th><th>Próxima Acción</th><th>Acciones</th>
            </tr></thead>
            <tbody>
              {filteredDeals.length === 0 ? (
                <tr><td colSpan={11} className="pip-empty">No hay deals</td></tr>
              ) : filteredDeals.map(deal => {
                const stageInfo = getStageInfo(deal.stage)
                const days = daysInStage(deal)
                const isStale = days > 14 && !['cerrado_ganado', 'cerrado_perdido'].includes(deal.stage)
                const prioInfo = PRIORITIES.find(p => p.value === deal.priority) || PRIORITIES[1]
                return (
                  <tr key={deal.$id} className={isStale ? 'pip-row-stale' : ''} onClick={() => openDetail(deal)} style={{ cursor: 'pointer' }}>
                    <td>{prioInfo.icon}</td>
                    <td className="pip-cell-title">{deal.title}</td>
                    <td>{deal.clientName || '—'}</td>
                    <td>{deal.owner ? deal.owner.split('@')[0] : '—'}</td>
                    <td>{deal.product || '—'}</td>
                    <td>{formatCurrency(deal.value)}</td>
                    <td>{deal.probability ?? 0}%</td>
                    <td><span className="pip-stage-badge" style={{ color: stageInfo.color, borderColor: stageInfo.color }}>{stageInfo.label}</span></td>
                    <td className={isStale ? 'pip-stale-text' : ''}>{days != null ? `${days}d` : '—'}</td>
                    <td className="pip-next-action-cell">{deal.nextAction || '—'}</td>
                    <td className="pip-actions-cell" onClick={e => e.stopPropagation()}>
                      <button onClick={() => openForm(deal)}><Edit3 size={14} /></button>
                      <button onClick={() => deleteDeal(deal.$id)}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ FORECAST ═══ */}
      {view === 'forecast' && (
        <div className="pip-forecast">
          <div className="pip-forecast-grid">
            <div className="pip-forecast-card">
              <h3><BarChart3 size={18} /> Pipeline por Etapa</h3>
              <div className="pip-funnel-list">
                {funnelData.map((item, i) => (
                  <div key={i} className="pip-funnel-row">
                    <span className="pip-funnel-label">{item.name}</span>
                    <div className="pip-funnel-bar-wrapper">
                      <div className="pip-funnel-bar" style={{
                        width: `${Math.max((item.count / Math.max(...funnelData.map(f => f.count), 1)) * 100, 8)}%`,
                        background: item.fill
                      }} />
                    </div>
                    <span className="pip-funnel-count">{item.count}</span>
                    <span className="pip-funnel-value">{formatCurrency(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="pip-forecast-card">
              <h3><TrendingUp size={18} /> Forecast Ponderado</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={forecastData} barGap={4}>
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                    formatter={(v) => [formatCurrency(v), '']} labelStyle={{ color: '#fff' }} />
                  <Bar dataKey="total" name="Total" radius={[4, 4, 0, 0]} opacity={0.3}>
                    {forecastData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Bar>
                  <Bar dataKey="weighted" name="Ponderado" radius={[4, 4, 0, 0]}>
                    {forecastData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="pip-forecast-card">
              <h3><Target size={18} /> Win / Loss</h3>
              <div className="pip-wl-grid">
                <div className="pip-wl-item pip-wl-won"><ArrowUpRight size={20} /><span className="pip-wl-num">{stats.won}</span><span className="pip-wl-label">Ganados</span><span className="pip-wl-value">{formatCurrency(stats.totalWon)}</span></div>
                <div className="pip-wl-item pip-wl-lost"><ArrowDownRight size={20} /><span className="pip-wl-num">{stats.lost}</span><span className="pip-wl-label">Perdidos</span><span className="pip-wl-value">{formatCurrency(stats.totalLost)}</span></div>
                <div className="pip-wl-item pip-wl-rate"><Target size={20} /><span className="pip-wl-num">{stats.convRate}%</span><span className="pip-wl-label">Win Rate</span></div>
                <div className="pip-wl-item pip-wl-avg"><BarChart3 size={20} /><span className="pip-wl-num">{formatCurrency(stats.avgDealSize)}</span><span className="pip-wl-label">Ticket Promedio</span></div>
              </div>
            </div>
            <div className="pip-forecast-card">
              <h3><AlertTriangle size={18} /> Deals Estancados (+14 días)</h3>
              <div className="pip-stale-list">
                {filteredDeals.filter(d => !['cerrado_ganado', 'cerrado_perdido'].includes(d.stage) && daysInStage(d) > 14)
                  .sort((a, b) => (daysInStage(b) || 0) - (daysInStage(a) || 0))
                  .map(deal => (
                    <div key={deal.$id} className="pip-stale-item" onClick={() => openDetail(deal)}>
                      <span className="pip-stale-days">{daysInStage(deal)}d</span>
                      <span className="pip-stale-title">{deal.title}</span>
                      <span className="pip-stage-badge" style={{ color: getStageInfo(deal.stage).color, borderColor: getStageInfo(deal.stage).color }}>{getStageInfo(deal.stage).label}</span>
                      <span className="pip-stale-value">{formatCurrency(deal.value)}</span>
                    </div>
                  ))}
                {filteredDeals.filter(d => !['cerrado_ganado', 'cerrado_perdido'].includes(d.stage) && daysInStage(d) > 14).length === 0 && (
                  <p className="pip-empty-text">🎉 No hay deals estancados</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DEAL DETAIL ═══ */}
      {view === 'detail' && selectedDeal && (
        <div className="pip-detail-overlay" onClick={() => setView('kanban')}>
          <div className="pip-detail" onClick={e => e.stopPropagation()}>
            <div className="pip-detail-header">
              <div>
                <h2>{selectedDeal.title}</h2>
                <span className="pip-stage-badge" style={{ color: getStageInfo(selectedDeal.stage).color, borderColor: getStageInfo(selectedDeal.stage).color }}>{getStageInfo(selectedDeal.stage).label}</span>
              </div>
              <div className="pip-detail-header-actions">
                <button className="pip-btn pip-btn-secondary" onClick={() => openForm(selectedDeal)}><Edit3 size={14} /> Editar</button>
                <button onClick={() => setView('kanban')}><X size={20} /></button>
              </div>
            </div>
            <div className="pip-detail-body">
              <div className="pip-detail-section">
                <h3>Información del Deal</h3>
                <div className="pip-detail-grid">
                  <div className="pip-detail-field"><span className="pip-detail-label">Valor</span><span className="pip-detail-value" style={{ color: '#4ade80', fontSize: '1.2rem' }}>{formatCurrency(selectedDeal.value)}</span></div>
                  <div className="pip-detail-field"><span className="pip-detail-label">Probabilidad</span><span className="pip-detail-value">{selectedDeal.probability || 0}%</span></div>
                  <div className="pip-detail-field"><span className="pip-detail-label">Producto</span><span className="pip-detail-value">{selectedDeal.product || '—'}</span></div>
                  <div className="pip-detail-field"><span className="pip-detail-label">Prioridad</span><span className="pip-detail-value">{(PRIORITIES.find(p => p.value === selectedDeal.priority) || PRIORITIES[1]).icon} {(PRIORITIES.find(p => p.value === selectedDeal.priority) || PRIORITIES[1]).label}</span></div>
                  <div className="pip-detail-field"><span className="pip-detail-label">Owner</span><span className="pip-detail-value">{selectedDeal.owner || 'Sin asignar'}</span></div>
                  <div className="pip-detail-field"><span className="pip-detail-label">Días en etapa</span><span className="pip-detail-value">{daysInStage(selectedDeal) ?? '—'} días</span></div>
                </div>
              </div>
              <div className="pip-detail-section">
                <h3>Contacto</h3>
                <div className="pip-detail-contact">
                  {selectedDeal.clientName && <div><User size={14} /> {selectedDeal.clientName}</div>}
                  {selectedDeal.clientEmail && <div><Mail size={14} /> {selectedDeal.clientEmail}</div>}
                  {selectedDeal.clientPhone && <div><Phone size={14} /> {selectedDeal.clientPhone}</div>}
                  {selectedDeal.lastContact && <div><Calendar size={14} /> Último contacto: {formatDateFull(selectedDeal.lastContact)}</div>}
                </div>
              </div>
              <div className="pip-detail-section">
                <h3>Progreso</h3>
                <div className="pip-stage-progress">
                  {ACTIVE_STAGES.map((s, i) => {
                    const currentIdx = ACTIVE_STAGES.findIndex(x => x.value === selectedDeal.stage)
                    const isActive = i <= currentIdx
                    const isCurrent = s.value === selectedDeal.stage
                    return (
                      <div key={s.value} className={`pip-progress-step ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}>
                        <div className="pip-progress-dot" style={{ background: isActive ? s.color : '#334155' }}
                          onClick={() => moveDeal(selectedDeal, s.value)} title={`Mover a ${s.label}`} />
                        <span>{s.label}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="pip-detail-section">
                <h3>Próxima Acción</h3>
                {selectedDeal.nextAction ? (
                  <div className="pip-detail-next-action"><CheckCircle size={14} /> {selectedDeal.nextAction}
                    {selectedDeal.nextActionDate && <span className="pip-detail-next-date">{formatDate(selectedDeal.nextActionDate)}</span>}
                  </div>
                ) : (
                  <div className="pip-quick-actions">
                    <button onClick={() => quickSetNextAction(selectedDeal, 'Llamar')}><Phone size={12} /> Llamar</button>
                    <button onClick={() => quickSetNextAction(selectedDeal, 'Enviar email')}><Mail size={12} /> Email</button>
                    <button onClick={() => quickSetNextAction(selectedDeal, 'Agendar demo')}><Calendar size={12} /> Demo</button>
                    <button onClick={() => quickSetNextAction(selectedDeal, 'Enviar propuesta')}><ArrowRight size={12} /> Propuesta</button>
                  </div>
                )}
              </div>
              {selectedDeal.notes && (
                <div className="pip-detail-section"><h3>Notas</h3><div className="pip-detail-notes">{selectedDeal.notes}</div></div>
              )}
              <div className="pip-detail-section">
                <h3><Calendar size={14} /> Actividades Relacionadas</h3>
                {dealActivities.length === 0 ? <p className="pip-empty-text">Sin actividades registradas</p> : (
                  <div className="pip-detail-timeline">
                    {dealActivities.map(a => (
                      <div key={a.$id} className="pip-timeline-item">
                        <div className="pip-timeline-dot" />
                        <div className="pip-timeline-content">
                          <span className="pip-timeline-title">{a.title}</span>
                          <span className="pip-timeline-date">{formatDate(a.date)}</span>
                          <span className="pip-timeline-type">{a.type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="pip-detail-footer">
              <button className="pip-btn pip-btn-secondary" onClick={() => deleteDeal(selectedDeal.$id)}><Trash2 size={14} /> Eliminar</button>
              <button className="pip-btn pip-btn-primary" onClick={() => openForm(selectedDeal)}><Edit3 size={14} /> Editar Deal</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ FORM ═══ */}
      {view === 'form' && (
        <div className="pip-form-overlay">
          <div className="pip-form">
            <div className="pip-form-header">
              <h2>{editingId ? 'Editar Deal' : 'Nuevo Deal'}</h2>
              <button onClick={() => setView('kanban')}><X size={20} /></button>
            </div>
            <div className="pip-form-body">
              <div className="pip-form-row">
                <label>Título *</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ej: GP-BOX para growshop Buenos Aires" />
              </div>
              <div className="pip-form-row-2col">
                <div className="pip-form-row"><label>Nombre Cliente</label><input value={form.clientName} onChange={e => setForm({ ...form, clientName: e.target.value })} /></div>
                <div className="pip-form-row"><label>Email</label><input value={form.clientEmail} onChange={e => setForm({ ...form, clientEmail: e.target.value })} /></div>
              </div>
              <div className="pip-form-row-2col">
                <div className="pip-form-row"><label>Teléfono</label><input value={form.clientPhone} onChange={e => setForm({ ...form, clientPhone: e.target.value })} placeholder="+54 11 ..." /></div>
                <div className="pip-form-row">
                  <label>Origen</label>
                  <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                    <option value="">— Seleccionar —</option>
                    <option value="web">Web</option><option value="instagram">Instagram</option>
                    <option value="referido">Referido</option><option value="feria">Feria/Evento</option>
                    <option value="revendedor">Revendedor</option><option value="otro">Otro</option>
                  </select>
                </div>
              </div>
              <div className="pip-form-row-2col">
                <div className="pip-form-row">
                  <label>Producto</label>
                  <select value={form.product} onChange={e => setForm({ ...form, product: e.target.value })}>
                    <option value="">— Seleccionar —</option>
                    {PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="pip-form-row"><label>Valor ($)</label><input type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} /></div>
              </div>
              <div className="pip-form-row-2col">
                <div className="pip-form-row">
                  <label>Etapa</label>
                  <select value={form.stage} onChange={e => {
                    const stg = STAGES.find(s => s.value === e.target.value)
                    setForm({ ...form, stage: e.target.value, probability: stg?.prob ?? form.probability })
                  }}>
                    {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="pip-form-row"><label>Probabilidad (%)</label><input type="number" min="0" max="100" value={form.probability} onChange={e => setForm({ ...form, probability: e.target.value })} /></div>
              </div>
              <div className="pip-form-row-2col">
                <div className="pip-form-row">
                  <label>Prioridad</label>
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.icon} {p.label}</option>)}
                  </select>
                </div>
                <div className="pip-form-row"><label>Último Contacto</label><input type="date" value={form.lastContact} onChange={e => setForm({ ...form, lastContact: e.target.value })} /></div>
              </div>
              <div className="pip-form-row-2col">
                <div className="pip-form-row"><label>Próxima Acción</label><input value={form.nextAction} onChange={e => setForm({ ...form, nextAction: e.target.value })} placeholder="Ej: Llamar, Enviar propuesta..." /></div>
                <div className="pip-form-row"><label>Fecha Próx. Acción</label><input type="date" value={form.nextActionDate} onChange={e => setForm({ ...form, nextActionDate: e.target.value })} /></div>
              </div>
              {form.stage === 'cerrado_perdido' && (
                <div className="pip-form-row">
                  <label>Motivo de Pérdida</label>
                  <select value={form.lossReason} onChange={e => setForm({ ...form, lossReason: e.target.value })}>
                    <option value="">— Seleccionar —</option>
                    {LOSS_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              )}
              <div className="pip-form-row"><label>Notas</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notas sobre el deal..." rows={3} /></div>
              <div className="pip-form-row">
                <label>Owner</label>
                <select value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })}>
                  <option value="">— Sin asignar —</option>
                  {adminUsers.map(u => <option key={u.$id} value={u.email}>{u.name || u.email}</option>)}
                </select>
              </div>
            </div>
            <div className="pip-form-footer">
              <button className="pip-btn pip-btn-secondary" onClick={() => setView('kanban')}><X size={16} /> Cancelar</button>
              <button className="pip-btn pip-btn-primary" onClick={saveDeal}><Save size={16} /> {editingId ? 'Guardar' : 'Crear Deal'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
